import { axiosFetch as authFetch } from '../../../services/axiosFetch';
import {
  DISPLAY_BASE,
  getDisplayToken,
  buildDisplayHeaders,
  parseDisplayResponse,
  messageFromDisplayErrorBody,
  displayGet,
  displayDelete,
  type DisplayUploadOptions,
} from '../../../services/displayApi';

export interface MediaLibraryUploadApiResponse {
  locationId: string;
  mediaId: string;
  status: number;
  message: string;
  url: string;
  mimeType: string;
  sizeBytes: number;
  orientation: 'LANDSCAPE' | 'PORTRAIT';
}

/** POST {DISPLAY_BASE}/display/upload-media?locationId= — orientation is 'LANDSCAPE' | 'PORTRAIT' (uppercase); omit/empty defaults to PORTRAIT server-side. */
export const uploadMediaLibraryApi = async (
  locationId: string,
  file: File,
  orientation?: 'LANDSCAPE' | 'PORTRAIT',
  options?: DisplayUploadOptions,
) => {
  const form = new FormData();
  form.append('media', file);
  if (orientation) form.append('orientation', orientation);
  const token = getDisplayToken();
  const res = await authFetch(`${DISPLAY_BASE}/display/upload-media?${new URLSearchParams({ locationId }).toString()}`, {
    method: 'POST',
    headers: buildDisplayHeaders(token, false),
    body: form,
    onUploadProgress: (event) => {
      const rawPercent =
        typeof event.progress === 'number'
          ? event.progress * 100
          : event.total
            ? (event.loaded / event.total) * 100
            : 0;
      const flooredPercent = Math.floor(rawPercent);
      if (flooredPercent >= 95) return;
      const percent = Math.max(1, flooredPercent);
      options?.onProgress?.(percent);
    },
  });
  const data: MediaLibraryUploadApiResponse = await parseDisplayResponse(res);
  if (!res.ok) {
    throw new Error(messageFromDisplayErrorBody(data, 'Failed to upload media'));
  }
  return { data };
};

export interface MediaLibraryListItemApi {
  id: string;
  locationId: string;
  name: string;
  mimeType: string;
  url: string;
  sizeBytes: number;
  /** Empty/invalid orientation on old/manual rows is normalized to 'PORTRAIT' server-side. */
  orientation: 'LANDSCAPE' | 'PORTRAIT';
  createdAt: string;
  updatedAt: string;
}

/** GET {DISPLAY_BASE}/display/media?locationId= */
export const fetchMediaLibraryApi = (locationId: string) =>
  displayGet(`/display/media?${new URLSearchParams({ locationId }).toString()}`);

export interface MediaLibraryDeleteApiResponse {
  mediaId: string;
  deleted: boolean;
}

/** DELETE {DISPLAY_BASE}/display/media/{mediaId}?locationId= — 409 (nothing deleted) if the media
 *  is in use by an active display or any playlist. displayDelete wraps the raw body as { data: rawBody },
 *  and rawBody is itself { data: <payload>, message } — callers must unwrap twice (res.data.data). */
export const deleteMediaLibraryApi = (mediaId: string, locationId: string) =>
  displayDelete(`/display/media/${encodeURIComponent(mediaId)}?${new URLSearchParams({ locationId }).toString()}`, 'Failed to delete media') as Promise<{ data: { data: MediaLibraryDeleteApiResponse; message?: string } }>;
